// src/components/admin/performance/TemplateManagement.js
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, Users, FileText, Star, AlertCircle } from 'lucide-react';
import performanceService from '../../../services/performance.service';
import TemplateEditor from './TemplateEditor';
import TemplateAssignmentModal from './TemplateAssignmentModal';

const TemplateManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [templateToAssign, setTemplateToAssign] = useState(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await performanceService.getTemplates();
            setTemplates(data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            setError('Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setSelectedTemplate(null);
        setShowEditor(true);
    };

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowEditor(true);
    };

    const handleDelete = async (templateId, templateName) => {
        if (!window.confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await performanceService.deleteTemplate(templateId);
            alert('Template deleted successfully');
            fetchTemplates();
        } catch (err) {
            console.error('Error deleting template:', err);
            alert(err.response?.data?.error || 'Failed to delete template. It may be in use by existing reviews.');
        }
    };

    const handleDuplicate = async (template) => {
        try {
            const duplicateData = {
                template_name: `${template.template_name} (Copy)`,
                description: template.description,
                fields: template.fields,
                is_default: false
            };
            await performanceService.createTemplate(duplicateData);
            alert('Template duplicated successfully');
            fetchTemplates();
        } catch (err) {
            console.error('Error duplicating template:', err);
            alert('Failed to duplicate template');
        }
    };

    const handleSetDefault = async (templateId) => {
        try {
            await performanceService.updateTemplate(templateId, { is_default: true });
            alert('Default template updated successfully');
            fetchTemplates();
        } catch (err) {
            console.error('Error setting default template:', err);
            alert('Failed to set default template');
        }
    };

    const handleAssign = (template) => {
        setTemplateToAssign(template);
        setShowAssignmentModal(true);
    };

    const handleEditorClose = (shouldRefresh) => {
        setShowEditor(false);
        setSelectedTemplate(null);
        if (shouldRefresh) {
            fetchTemplates();
        }
    };

    const handleAssignmentClose = () => {
        setShowAssignmentModal(false);
        setTemplateToAssign(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Review Templates</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Create and manage customizable review templates for different roles and departments
                    </p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                </button>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Templates Grid */}
            {templates.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No templates yet</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">Get started by creating your first review template</p>
                    <button
                        onClick={handleCreateNew}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Template
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template) => (
                        <div
                            key={template.id}
                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow"
                        >
                            {/* Template Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {template.template_name}
                                        </h3>
                                        {template.is_default && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                                <Star className="w-3 h-3 mr-1" />
                                                Default
                                            </span>
                                        )}
                                    </div>
                                    {template.description && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                            {template.description}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Template Info */}
                            <div className="space-y-2 mb-4">
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <FileText className="w-4 h-4 mr-2" />
                                    {Array.isArray(template.fields) ? template.fields.length : 0} fields
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                    Created {new Date(template.created_at).toLocaleDateString()}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => handleEdit(template)}
                                    className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <Edit className="w-4 h-4 mr-1" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleAssign(template)}
                                    className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                >
                                    <Users className="w-4 h-4 mr-1" />
                                    Assign
                                </button>
                                <div className="relative group">
                                    <button className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                        </svg>
                                    </button>
                                    {/* Dropdown Menu */}
                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                        <div className="py-1">
                                            {!template.is_default && (
                                                <button
                                                    onClick={() => handleSetDefault(template.id)}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                                >
                                                    <Star className="w-4 h-4 mr-2" />
                                                    Set as Default
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDuplicate(template)}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                            >
                                                <Copy className="w-4 h-4 mr-2" />
                                                Duplicate
                                            </button>
                                            {!template.is_default && (
                                                <button
                                                    onClick={() => handleDelete(template.id, template.template_name)}
                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Template Editor Modal */}
            {showEditor && (
                <TemplateEditor
                    template={selectedTemplate}
                    onClose={handleEditorClose}
                />
            )}

            {/* Template Assignment Modal */}
            {showAssignmentModal && templateToAssign && (
                <TemplateAssignmentModal
                    template={templateToAssign}
                    onClose={handleAssignmentClose}
                />
            )}
        </div>
    );
};

export default TemplateManagement;
